import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material'
import { RailTrackResponseDto } from '@planning/app/api'
import { useTranslate } from '@tolgee/react'
import { FC, useState } from 'react'

interface Props {
  open: boolean
  trackName: string
  availableTracks: RailTrackResponseDto[]
  onConfirm: (targetTrackId: string | null) => void
  onCancel: () => void
}

export const RailTrackDeselectDialog: FC<Props> = ({
  open,
  trackName,
  availableTracks,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslate()

  const isSingleOption = availableTracks.length === 1
  const noOptionsLeft = availableTracks.length === 0
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(
    isSingleOption ? availableTracks[0].id : null,
  )

  const handleConfirm = () => {
    if (selectedTargetId || noOptionsLeft) {
      onConfirm(selectedTargetId)
    }
  }

  return (
    <Dialog open={open} onClose={onCancel} maxWidth='xs' fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold' }}>
        {noOptionsLeft
          ? t('removeLastTrack', 'Remove last track {trackName}?', { trackName })
          : t('reassignTitle', 'Reassign containers from {trackName}?', { trackName })}
      </DialogTitle>

      <DialogContent>
        <DialogContentText sx={{ color: 'text.primary', mb: 2 }}>
          {noOptionsLeft
            ? t(
                'removeLastTrackWarning',
                'Since this is the last track, all containers and railcars assigned to {trackName} will be unassigned.',
                { trackName },
              )
            : isSingleOption
              ? t(
                  'reassignSingleWarning',
                  'All containers and railcars assigned to {trackName} will be reassigned to the last available track: {targetTrack}.',
                  { trackName, targetTrack: availableTracks[0].name },
                )
              : t(
                  'reassignMultipleWarning',
                  'Select the rail track to which containers from {trackName} should be reassigned.',
                  { trackName },
                )}
        </DialogContentText>

        {!isSingleOption && (
          <RadioGroup value={selectedTargetId} onChange={e => setSelectedTargetId(e.target.value)}>
            {availableTracks.map(track => (
              <FormControlLabel
                key={track.id}
                value={track.id}
                control={<Radio size='small' />}
                label={track.name}
              />
            ))}
          </RadioGroup>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, flexDirection: 'row', gap: 1 }}>
        <Button onClick={handleConfirm} variant='contained' color='primary'>
          {t('yesReassign', 'Yes, reassign')}
        </Button>

        <Button
          onClick={onCancel}
          color='inherit'
          sx={{
            color: 'text.primary',
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {t('noBackToAssignment', 'No, back to track assignment')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
